import {
  mapPricingModel,
  mapLegacyAdBudgetType,
} from 'src/App/utils/analytics/event';
import type { PricingModel } from 'src/App/generated/graphql-types';
import { centsToDollars } from 'src/App/utils/price';
import type { AdvertiserContract } from 'src/App/types/advertiser';
import type { BudgetOption } from '../mapBudgetOption';
import type { TrackingProps } from '@seek/tealium-adapter/lib-types/types';

interface AdBudgetSelectionPageBaseEvent extends TrackingProps {
  activityId?: string;
  adBudgetPrices?: number[];
  adBudgetType?: string;
  advertiserCommitmentType?: string;
  currentPage?: string;
  experienceId?: string;
  experiments?: Array<Record<string, string | undefined>>;
  contractProductSetId?: string;
  contractProductPriceQuery?: ContractProductPriceQuery[];
  pricingType?: string;
}

interface BuyNowPressedEvent extends AdBudgetSelectionPageBaseEvent {
  productCategories: string[];
  productEvents: 'add';
  productIds: string[];
  productTotalPrice: number[];
  contractProductSetProductId: string;
  contractProductName: string;
}

interface AdBudgetSelectionPageBaseInput {
  activityId?: string;
  adBudgetType: string;
  adBudgetsList: BudgetOption[];
  advertiserContracts?: AdvertiserContract[];
  experienceId?: string;
  pricingModel?: PricingModel | null;
  productSetId: string;
  userId?: string;
  currentPage?: string;
}

interface ContractProductPriceQuery {
  position: number;
  id: string;
  price: number;
  currency: string;
  type: 'FlexibleBudget' | 'BrandedBudget';
}

const mapBudgetOptionsToBudgetPrices = (
  adBudgetsList: BudgetOption[],
): number[] =>
  adBudgetsList.map(({ budgetValue }) => centsToDollars(budgetValue));

const mapAdBudgetsListToTrackingPayload = (adBudgets: BudgetOption[]) =>
  adBudgets.map((adBudget, position) => ({
    position: position + 1,
    id: adBudget.budgetId,
    price: adBudget.budgetValue,
    currency: adBudget.currency,
    type: adBudget.budgetType,
  }));

const createAdBudgetSelectionPageBaseEvent = ({
  activityId,
  adBudgetType,
  adBudgetsList,
  experienceId,
  pricingModel,
  productSetId,
  currentPage,
}: AdBudgetSelectionPageBaseInput): AdBudgetSelectionPageBaseEvent => ({
  adBudgetPrices: mapBudgetOptionsToBudgetPrices(
    adBudgetsList.filter(
      (budgetOption) => budgetOption.budgetType === adBudgetType,
    ),
  ),
  adBudgetType: mapLegacyAdBudgetType(adBudgetType),
  ...(activityId && { activityId }),
  ...(experienceId && { experienceId }),
  ...(activityId &&
    experienceId && {
      experiments: [
        {
          id: activityId,
          variant: experienceId,
        },
      ],
    }),
  ...(pricingModel && {
    pricingType: mapPricingModel(pricingModel),
  }),
  contractProductSetId: productSetId,
  contractProductPriceQuery: mapAdBudgetsListToTrackingPayload(adBudgetsList),
  currentPage,
});

const createBuyNowPressedEvent = (
  selectedBudget: BudgetOption,
): BuyNowPressedEvent => ({
  adBudgetPrices: mapBudgetOptionsToBudgetPrices([selectedBudget]),
  productCategories: ['contract'],
  productEvents: 'add',
  contractProductSetProductId: selectedBudget.budgetId,
  contractProductName: selectedBudget.budgetName,
  productIds: [mapLegacyAdBudgetType(selectedBudget.budgetType)],
  productTotalPrice: mapBudgetOptionsToBudgetPrices([selectedBudget]),
});

export {
  type AdBudgetSelectionPageBaseEvent,
  type AdBudgetSelectionPageBaseInput,
  type BuyNowPressedEvent,
  createAdBudgetSelectionPageBaseEvent,
  createBuyNowPressedEvent,
  mapBudgetOptionsToBudgetPrices,
};
